import { useState } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { useHomeStats } from '../hooks/useHomeStats';

type PeriodKey = '7d' | '30d' | '90d';

const PERIOD_OPTIONS: Array<{ key: PeriodKey; label: string }> = [
  { key: '7d', label: '近7天' },
  { key: '30d', label: '近30天' },
  { key: '90d', label: '近3个月' },
];

function formatAmount(v: number) {
  if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
  return String(v);
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
}

function CustomTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 rounded-xl px-3.5 py-2.5 text-xs">
      <p className="text-gray-400 mb-1.5 font-medium">{label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 mb-1 last:mb-0">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />
          <span className="text-gray-500">{p.name}</span>
          <span className="font-semibold text-gray-800 ml-auto pl-4">
            {p.name === '收入(元)' ? `¥${formatAmount(p.value)}` : `${p.value} 单`}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function OrderTrends() {
  const [period, setPeriod] = useState<PeriodKey>('30d');
  const { data, loading } = useHomeStats();

  const trendData =
    period === '7d' ? data?.trend7 :
    period === '90d' ? data?.trend90 :
    data?.trend30;

  const hasData = trendData && trendData.some(d => d.orders > 0 || d.income > 0);

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 md:mb-5">
        <h3 className="text-sm md:text-base font-semibold text-gray-900">订单趋势</h3>
        <div className="flex items-center bg-gray-50 rounded-lg p-0.5 gap-0.5">
          {PERIOD_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setPeriod(key)}
              className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors cursor-pointer whitespace-nowrap ${
                period === key
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="h-52 md:h-64">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-2xl text-gray-200" />
            </div>
          </div>
        ) : !hasData ? (
          <div className="h-full flex flex-col items-center justify-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <i className="ri-line-chart-line text-3xl text-gray-200" />
            </div>
            <p className="text-xs text-gray-400">该周期暂无订单数据</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trendData} margin={{ top: 5, right: 4, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="homeIncomeGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.18} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="homeOrdersGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#06b6d4" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#06b6d4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                interval={period === '30d' ? 4 : 'preserveStartEnd'}
              />
              <YAxis
                yAxisId="income"
                orientation="right"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={formatAmount}
                width={40}
              />
              <YAxis
                yAxisId="orders"
                orientation="left"
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                axisLine={false}
                tickLine={false}
                allowDecimals={false}
                width={26}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                iconType="circle"
                iconSize={8}
              />
              <Area
                yAxisId="orders"
                type="monotone"
                dataKey="orders"
                name="订单量(单)"
                stroke="#06b6d4"
                strokeWidth={2}
                fill="url(#homeOrdersGrad)"
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Area
                yAxisId="income"
                type="monotone"
                dataKey="income"
                name="收入(元)"
                stroke="#10b981"
                strokeWidth={2}
                fill="url(#homeIncomeGrad)"
                dot={false}
                activeDot={{ r: 4 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
